import React, { useState, useRef, useEffect } from "react";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import { DownArrow } from "../icons";
import { hardcodedMonths } from "../../constants/period";
import "./glowMonthDropdown.scss";

function GlowMonthSelect({
  value,
  onChange,
  isOpen,
  onToggle,
  fieldError,
  className,
  currentContextType,
  onFocus,
  resetMonthDropdown, // triggers reset when true
  onResetMonthComplete, // callback to notify parent when reset is complete
}) {

  const dropdownRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(value);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [glow, setGlow] = useState(false);
   // Combine classes; when glow is active, add glow classes.
   const containerClasses = `month-select-glow ${className} ${
    glow ? "glow-on-animation show-animation" : ""
  }`;


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [onToggle]);

  // Reset dropdown if resetMonthDropdown becomes true
  useEffect(() => {
    if (resetMonthDropdown) {
      clearMonth();
      if (onResetMonthComplete) onResetMonthComplete();
    }
  }, [resetMonthDropdown, onResetMonthComplete]);

  // Update search-based focused index
  useEffect(() => {
    if (searchTerm.length > 0) {
      const index = hardcodedMonths.findIndex((month) =>
        new RegExp(`^${searchTerm}`, "i").test(month)
      );
      if (index !== -1) {
        setFocusedIndex(index);
      }
    }
  }, [searchTerm]);

  // Scroll the focused item into view
  useEffect(() => {
    if (dropdownRef.current) {
      const activeItem = dropdownRef.current.querySelector(".focused");
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [focusedIndex]);

  // When dropdown opens, set focusedIndex to selected month (if any)
  useEffect(() => {
    if (isOpen && selectedMonth !== "") {
      setFocusedIndex(selectedMonth - 1);
    }
  }, [isOpen, selectedMonth]);

  const clearMonth = () => {
    setSelectedMonth("");
    localStorage.removeItem(getStorageKey("ccExpMonth", currentContextType));
  };

  const handleMonthChange = (monthIndex) => {
    setSelectedMonth(monthIndex);
    onChange(monthIndex);
  };

  const handleDropdownKeyNavigation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const updateIndex = (currentIndex, increment) => {
      const newIndex = currentIndex + increment;
      return newIndex < 0
        ? hardcodedMonths.length - 1
        : newIndex >= hardcodedMonths.length
          ? 0
          : newIndex;
    };
    if (e.key === "ArrowDown") {
      setIsKeyboardNavigation(true);
      setFocusedIndex(updateIndex(focusedIndex, 1));
    } else if (e.key === "ArrowUp") {
      setIsKeyboardNavigation(true);
      setFocusedIndex(updateIndex(focusedIndex, -1));
    } else if (e.key === "Enter") {
      handleMonthChange(focusedIndex + 1);
      onToggle(false);
    } else if (/^[a-z0-9]$/i.test(e.key)) {
      setSearchTerm(e.key);
      setTimeout(() => {
        setSearchTerm("");
      }, 1000);
    }
  };

  const handleMouseOver = (index) => {
    if (!isKeyboardNavigation) {
      setFocusedIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isKeyboardNavigation) {
      setFocusedIndex(-1);
    }
  };


  // Function to trigger glow and then toggle dropdown open after a short delay.
  const handleHeaderClick = () => {
    setGlow(true);
    // Adjust delay to match your desired timing (e.g., 200ms)
    setTimeout(() => {
      setGlow(false);
      onToggle(!isOpen);
    }, 500);
  };
  const handleFocus = () => {
    if (onFocus) onFocus();
  };
 
  return (
    <div
      className={containerClasses}
      onKeyDown={handleDropdownKeyNavigation}
      ref={dropdownRef}
    >
      <div className="month-select-glow-header" onClick={handleHeaderClick}>
        <div className="year-select-content">
          {!selectedMonth ? (
            <div className="month-placeholder">
              {fieldError ? fieldError : "Select an Expired Month"}
            </div>
          ) : (
            <div className="selected-month">
              {hardcodedMonths[selectedMonth - 1]}
            </div>
          )}
          <DownArrow fillColor="var(--color-bloodred)" />
        </div>
      </div>
      {isOpen && (
        <div className="month-dropdown-options">
          {hardcodedMonths.map((month, index) => (
            <div
              key={month}
              onClick={() => {
                handleMonthChange(index + 1);
                onToggle(false);
              }}
              tabIndex={0}
              ref={(el) => {
                if (focusedIndex === index && el) el.focus();
              }}
              onFocus={handleFocus}
              className={index === focusedIndex ? "focused" : ""}
              onMouseOver={() => handleMouseOver(index)}
              onMouseLeave={handleMouseLeave}
            >
              {month}
            </div>
          ))}
        </div>
      )}
      {selectedMonth !== "" && (
        <button type="button" onClick={clearMonth} className="clear-month-btn">
          Clear
        </button>
      )}
    </div>
  );
}

export default GlowMonthSelect;
